// Détection d'anomalies — surveillance automatique des données critiques
import { useState, useMemo } from "react";
import { AlertTriangle, CheckCircle, Eye } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { useAnomalies, useAcknowledgeAnomaly, useResolveAnomaly } from "@/hooks/admin/useAI";
import type { Anomaly, AnomalyType, AnomalySeverity, AnomalyStatus } from "@/lib/admin-api/ai";
import React from "react";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(n: number): string {
  return `${n.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

const SEVERITY_BADGE: Record<AnomalySeverity, string> = {
  critical: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-stone-100 text-stone-600",
};

const SEVERITY_LABELS: Record<AnomalySeverity, string> = {
  critical: "Critique",
  high: "Élevée",
  medium: "Modérée",
  low: "Faible",
};

const STATUS_BADGE: Record<AnomalyStatus, string> = {
  new: "bg-red-100 text-red-700",
  acknowledged: "bg-amber-100 text-amber-700",
  resolved: "bg-green-100 text-green-700",
};

const STATUS_LABELS: Record<AnomalyStatus, string> = {
  new: "Nouveau",
  acknowledged: "En investigation",
  resolved: "Résolu",
};

const TYPE_LABELS: Record<AnomalyType, string> = {
  payment: "Paiement",
  order: "Commande",
  inventory: "Inventaire",
  agent: "Agent",
};

const ANOMALY_TYPES: readonly { value: AnomalyType; label: string }[] = [
  { value: "payment", label: "Paiement" },
  { value: "order", label: "Commande" },
  { value: "inventory", label: "Inventaire" },
  { value: "agent", label: "Agent" },
];

const SEVERITY_LEVELS: readonly { value: AnomalySeverity; label: string }[] = [
  { value: "critical", label: "Critique" },
  { value: "high", label: "Élevée" },
  { value: "medium", label: "Modérée" },
  { value: "low", label: "Faible" },
];

const STATUS_LEVELS: readonly { value: AnomalyStatus; label: string }[] = [
  { value: "new", label: "Nouveau" },
  { value: "acknowledged", label: "En investigation" },
  { value: "resolved", label: "Résolu" },
];

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function AnomalyDetection() {
  const [typeFilter, setTypeFilter] = useState<AnomalyType | "">("");
  const [severityFilter, setSeverityFilter] = useState<AnomalySeverity | "">("");
  const [statusFilter, setStatusFilter] = useState<AnomalyStatus | "">("");

  const params = useMemo(
    () => ({
      type: (typeFilter || null) as AnomalyType | null,
      severity: (severityFilter || null) as AnomalySeverity | null,
      status: (statusFilter || null) as AnomalyStatus | null,
    }),
    [typeFilter, severityFilter, statusFilter],
  );

  const { data, isLoading, isError } = useAnomalies(params);
  const acknowledge = useAcknowledgeAnomaly();
  const resolve = useResolveAnomaly();

  const anomalies = data?.anomalies ?? [];
  const newCount = data?.new_count ?? 0;
  const criticalCount = anomalies.filter((a) => a.severity === "critical").length;
  const investigatingCount = anomalies.filter((a) => a.status === "acknowledged").length;
  const resolvedCount = anomalies.filter((a) => a.status === "resolved").length;

  const columns: readonly ColumnDef<Anomaly>[] = useMemo(
    () => [
      {
        id: "type",
        header: "Type",
        cell: (row) => (
          <span className="inline-block rounded bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600">
            {TYPE_LABELS[row.type]}
          </span>
        ),
      },
      {
        id: "description",
        header: "Description",
        cell: (row) => (
          <p
            className={`text-sm text-stone-700 ${row.status === "new" ? "font-medium" : ""}`}
          >
            {row.description}
          </p>
        ),
      },
      {
        id: "value",
        header: "Valeur détectée",
        cell: (row) => (
          <div>
            <p className="text-sm font-medium text-stone-900">{formatFcfa(row.value)}</p>
            <p className="text-xs text-stone-400">Attendu : {formatFcfa(row.expected_value)}</p>
          </div>
        ),
      },
      {
        id: "deviation_percent",
        header: "Écart",
        cell: (row) => (
          <span
            className={`text-sm font-medium ${row.deviation_percent > 20 ? "text-red-600" : "text-amber-600"}`}
          >
            +{row.deviation_percent.toFixed(1)}%
          </span>
        ),
      },
      {
        id: "severity",
        header: "Sévérité",
        cell: (row) => (
          <span
            className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${SEVERITY_BADGE[row.severity]}`}
          >
            {SEVERITY_LABELS[row.severity]}
          </span>
        ),
      },
      {
        id: "detected_at",
        header: "Détecté le",
        cell: (row) => (
          <span className="text-sm text-stone-500">{formatDate(row.detected_at)}</span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (row) => (
          <span
            className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[row.status]}`}
          >
            {STATUS_LABELS[row.status]}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: (row) => (
          <div className="flex gap-1">
            {row.status === "new" && (
              <button
                type="button"
                disabled={acknowledge.isPending}
                onClick={() => acknowledge.mutate(row.id)}
                className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-200 disabled:opacity-60"
              >
                <Eye className="h-3.5 w-3.5" />
                Reconnaître
              </button>
            )}
            {row.status !== "resolved" && (
              <button
                type="button"
                disabled={resolve.isPending}
                onClick={() => resolve.mutate(row.id)}
                className="inline-flex items-center gap-1 rounded-md bg-green-100 px-2.5 py-1.5 text-xs font-medium text-green-700 hover:bg-green-200 disabled:opacity-60"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Résoudre
              </button>
            )}
          </div>
        ),
      },
    ],
    [acknowledge, resolve],
  );

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Détection d'anomalies</h1>
          <p className="mt-1 text-sm text-stone-500">
            Surveillance automatique des paiements, commandes, inventaires et agents
          </p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="Nouvelles anomalies"
            value={isLoading ? "…" : String(newCount)}
            icon={<AlertTriangle className="h-4 w-4" />}
          />
          <KPICard
            label="Critiques"
            value={isLoading ? "…" : String(criticalCount)}
            icon={<AlertTriangle className="h-4 w-4" />}
          />
          <KPICard
            label="En investigation"
            value={isLoading ? "…" : String(investigatingCount)}
            icon={<Eye className="h-4 w-4" />}
          />
          <KPICard
            label="Résolues ce mois"
            value={isLoading ? "…" : String(resolvedCount)}
            icon={<CheckCircle className="h-4 w-4" />}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as AnomalyType | "")}
            className="rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
          >
            <option value="">Tous les types</option>
            {ANOMALY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as AnomalySeverity | "")}
            className="rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
          >
            <option value="">Toutes les sévérités</option>
            {SEVERITY_LEVELS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AnomalyStatus | "")}
            className="rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
          >
            <option value="">Tous les statuts</option>
            {STATUS_LEVELS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        {isError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Impossible de charger les anomalies. Veuillez réessayer.
          </div>
        ) : (
          <AdminDataTable
            data={anomalies}
            columns={columns}
            isLoading={isLoading}
            emptyMessage="Aucune anomalie détectée — tous les indicateurs sont dans les normes."
          />
        )}
      </div>
    </AdminLayout>
  );
}
